import { refundAdminPayment } from '@/api/admin';
import { PaymentsAdmin } from '@/types';
import { useMutation } from '@tanstack/react-query';
import { Button, Drawer, message, Modal, Typography } from 'antd';
import { WalletOutlined } from '@ant-design/icons';
import { format } from 'date-fns';
import { useEffect, useState } from 'react';
import './RefundPaymentPanel.scss';

interface Props {
  open: boolean;
  payment: PaymentsAdmin | null;
  onClose: () => void;
  onSuccess: () => void;
}

export default function RefundPaymentPanel({
  open,
  payment,
  onClose,
  onSuccess,
}: Props) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);

  useEffect(() => {
    if (!open) {
      setConfirmOpen(false);
      setSuccessOpen(false);
    }
  }, [open]);

  const { mutateAsync: processRefund, isPending } = useMutation({
    mutationFn: (sessionId: string) => refundAdminPayment(sessionId),
    onSuccess: () => {
      setConfirmOpen(false);
      setSuccessOpen(true);
    },
    onError: (error: any) => {
      message.error(error?.message || 'Failed to process refund');
    },
  });

  if (!payment) return null;

  const consultantName = `${payment.consultant?.firstName ?? ''} ${
    payment.consultant?.lastName ?? ''
  }`.trim();
  const franchisorName = payment.franchise?.company || '—';
  const meetingDate = payment.start
    ? format(new Date(payment.start), 'd MMM, yyyy, h:mm a')
    : 'N/A';
  const amountLabel =
    typeof payment.paidAmount === 'number'
      ? `$${payment.paidAmount.toFixed(2)}`
      : '—';

  const handleProcessClick = () => setConfirmOpen(true);

  const handleConfirmRefund = async () => {
    try {
      await processRefund(payment.id);
    } catch {
      // onError already surfaces the message
    }
  };

  const handleDone = () => {
    setSuccessOpen(false);
    onSuccess();
  };

  return (
    <>
      <Drawer
        open={open}
        onClose={onClose}
        placement="right"
        width={480}
        title="Process Refund"
        className="refund-panel"
        destroyOnClose
      >
        <Typography.Text className="refund-panel__subtitle">
          Refund payment for <strong>{consultantName}</strong>
        </Typography.Text>

        <div className="refund-panel__details">
          <div className="refund-panel__row">
            <span className="refund-panel__label">Consultant</span>
            <span className="refund-panel__value">{consultantName}</span>
          </div>
          <div className="refund-panel__row">
            <span className="refund-panel__label">Franchisor</span>
            <span className="refund-panel__value">{franchisorName}</span>
          </div>
          <div className="refund-panel__row">
            <span className="refund-panel__label">Meeting Date</span>
            <span className="refund-panel__value">{meetingDate}</span>
          </div>
          <div className="refund-panel__row">
            <span className="refund-panel__label">Total Paid</span>
            <span className="refund-panel__value">{amountLabel}</span>
          </div>
          <div className="refund-panel__row refund-panel__row--emphasis">
            <span className="refund-panel__label">
              Refund Amount (Full Refund)
            </span>
            <span className="refund-panel__value">{amountLabel}</span>
          </div>
        </div>

        <div className="refund-panel__actions">
          <Button onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button type="primary" onClick={handleProcessClick} disabled={isPending}>
            Process Refund
          </Button>
        </div>
      </Drawer>

      <Modal
        open={confirmOpen}
        onCancel={() => !isPending && setConfirmOpen(false)}
        footer={null}
        centered
        width={400}
        title="Confirm Refund"
        className="refund-confirm-modal"
        closable={!isPending}
        maskClosable={!isPending}
      >
        <Typography.Text className="refund-confirm-modal__body">
          Are you sure you want to process a full refund of{' '}
          <strong>{amountLabel}</strong> for <strong>{consultantName}</strong>?
          This action cannot be undone.
        </Typography.Text>
        <div className="refund-confirm-modal__actions">
          <Button
            type="primary"
            block
            loading={isPending}
            onClick={handleConfirmRefund}
          >
            Process Refund
          </Button>
          <Button
            block
            onClick={() => setConfirmOpen(false)}
            disabled={isPending}
          >
            Cancel
          </Button>
        </div>
      </Modal>

      <Modal
        open={successOpen}
        onCancel={handleDone}
        footer={null}
        centered
        width={420}
        className="refund-success-modal"
        closable
      >
        <div className="refund-success-modal__icon">
          <WalletOutlined />
        </div>
        <Typography.Title level={4} className="refund-success-modal__title">
          Refund Processed
        </Typography.Title>
        <Typography.Text className="refund-success-modal__body">
          A full refund of <strong>{amountLabel}</strong> has been successfully
          processed for <strong>{consultantName}</strong>.
        </Typography.Text>
        <div className="refund-success-modal__actions">
          <Button type="primary" onClick={handleDone}>
            Done
          </Button>
        </div>
      </Modal>
    </>
  );
}
